/**
 * Hook to extend reported line with future points from predictions
 *
 * After filtering, extends the reported line with any future-dated points
 * from predictions['linear-regression'] that don't already exist in reported.
 * This happens BEFORE cumulative transformation.
 */

import { useMemo } from 'react';
import type { UnifiedForecastResult, RebatePoint } from '@erp/shared-types';

/**
 * Helper to convert date to Date object if it's a string
 */
function toDate(date: Date | string | undefined): Date | null {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date === 'string') return new Date(date);
  return null;
}

/**
 * Helper to get timestamp from date (handles both Date and string)
 */
function getDateTimestamp(date: Date | string | undefined): number | null {
  const dateObj = toDate(date);
  return dateObj ? dateObj.getTime() : null;
}

/**
 * Result of extending reported line with future predictions
 */
export interface ExtendedReportedResult {
  forecastData: UnifiedForecastResult | null | undefined;
  reportedSplitTimestamp: number | null; // Timestamp of the last real reported date (before extension)
}

/**
 * Extend reported line with future points from linear-regression predictions
 *
 * @param forecastData - Filtered forecast data
 * @returns Extended forecast data and split timestamp
 */
function extendReportedWithFuturePredictions(
  forecastData: UnifiedForecastResult | null | undefined,
): ExtendedReportedResult {
  if (!forecastData) {
    return {
      forecastData,
      reportedSplitTimestamp: null,
    };
  }

  const reported = forecastData.reported || [];
  const linearRegressionPredictions =
    forecastData.predictions?.['linear-regression'] || [];

  // If no reported data or no predictions, return as-is
  if (reported.length === 0 || linearRegressionPredictions.length === 0) {
    return {
      forecastData,
      reportedSplitTimestamp: null,
    };
  }

  // Find the latest date in reported (find actual max, don't assume sorted)
  // Handle both Date objects and date strings
  const reportedDates = reported
    .map((point) => getDateTimestamp(point.date))
    .filter((time): time is number => time !== null);

  if (reportedDates.length === 0) {
    return {
      forecastData,
      reportedSplitTimestamp: null,
    };
  }

  const maxReportedDate = Math.max(...reportedDates);
  const lastReportedDate = new Date(maxReportedDate);
  const reportedSplitTimestamp = maxReportedDate; // Store the split point

  // Find all prediction points that are in the future (after last reported date)
  const futurePredictions = linearRegressionPredictions.filter((point) => {
    const pointTimestamp = getDateTimestamp(point.date);
    if (pointTimestamp === null) return false;
    return pointTimestamp > lastReportedDate.getTime();
  });

  // If no future predictions, return as-is
  if (futurePredictions.length === 0) {
    return {
      forecastData,
      reportedSplitTimestamp,
    };
  }

  // Create a map of existing reported dates for quick lookup (reuse the dates we already computed)
  const reportedDateSet = new Set(reportedDates);

  // Add future predictions that don't already exist in reported
  const extendedReported: RebatePoint[] = [...reported];
  futurePredictions.forEach((predictionPoint) => {
    const predictionTimestamp = getDateTimestamp(predictionPoint.date);
    if (
      predictionTimestamp !== null &&
      !reportedDateSet.has(predictionTimestamp)
    ) {
      extendedReported.push(predictionPoint);
      reportedDateSet.add(predictionTimestamp);
    }
  });

  // Sort by date to ensure chronological order (handle both Date and string)
  extendedReported.sort((a, b) => {
    const timestampA = getDateTimestamp(a.date);
    const timestampB = getDateTimestamp(b.date);
    if (timestampA === null || timestampB === null) return 0;
    return timestampA - timestampB;
  });

  // Store the split timestamp as metadata on the forecast data
  const extendedForecastData: UnifiedForecastResult & {
    _reportedSplitTimestamp?: number;
  } = {
    ...forecastData,
    reported: extendedReported,
    _reportedSplitTimestamp: reportedSplitTimestamp,
  };

  return {
    forecastData: extendedForecastData,
    reportedSplitTimestamp,
  };
}

/**
 * Hook to extend reported line with future predictions
 *
 * @param forecastData - Filtered forecast data
 * @returns Forecast data with extended reported line and split timestamp
 */
export function useExtendedReportedForecast(
  forecastData: UnifiedForecastResult | null | undefined,
): ExtendedReportedResult {
  return useMemo(() => {
    return extendReportedWithFuturePredictions(forecastData);
  }, [forecastData]);
}
